//  El núcleo: las utilerías que usa todo el resto, y la conexión a Supabase
//  re-exportada de `cliente.ts`. Ése es el ÚNICO módulo del que depende —y
//  `cliente.ts` no importa a nadie de acá—, así que no hay ciclo y el núcleo
//  se sigue evaluando primero.

import type { Database } from './tipos/supabase';

//  El cliente vive en `cliente.ts` para que las pruebas de pantalla lo
//  reemplacen entero por `cliente.mock.ts`. Los demás módulos siguen
//  pidiéndolo con `import { sb } from './nucleo'`.
import { sb } from './cliente';
export { sb };

//  Lo que devuelve $(): un elemento del que se puede leer .value, .checked,
//  .options, .reset()… sin castear en cada línea. Es laxo a propósito: el
//  rigor del DOM se sube por módulo cuando se lo toque a fondo (D81, regla 4).
//
//  OJO: NO escribirlo como intersección de HTMLInputElement & HTMLSelectElement
//  & …: `type` tiene literales distintos y TypeScript colapsa todo a `never`.
export type Elemento = {
  //  closest() del DOM devuelve Element, sin .value ni .dataset. Va PRIMERO:
  //  TypeScript prueba las firmas de la intersección en orden y ésta le gana
  //  a la de HTMLElement.
  closest(sel: string): Elemento;
} & HTMLElement & {
  value: string; checked: boolean; disabled: boolean; files: FileList | null;
  selectedIndex: number; options: HTMLOptionsCollection;
  selectedOptions: HTMLCollectionOf<HTMLOptionElement>;
  reset(): void; submit(): void; requestSubmit(): void; select(): void;
};
//  El segundo argumento es la raíz: $('#coMonto', panel) reemplaza a
//  panel.querySelector('#coMonto'), que devuelve Element y no tiene .value.
export const $  = (s: string, raiz: ParentNode = document) =>
  raiz.querySelector(s) as Elemento;
export const $$ = (s: string, raiz: ParentNode = document) =>
  raiz.querySelectorAll(s) as NodeListOf<Elemento>;
//  Para ev.target, que es EventTarget y no tiene ni .value ni .closest.
export const el = (x: EventTarget | Element | null) => x as Elemento;

export const esc = (t: unknown) => String(t ?? '').replace(/[&<>"]/g, c =>
  ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;'} as Record<string, string>)[c]);

//  aviso() ESCAPA siempre lo que se le pasa: el `error.message` de PostgREST
//  puede arrastrar texto de un usuario (nombre de cliente, motivo). Si hacen
//  falta marcas (<br>, <b>) se usa avisoHtml() y se escapa cada dato a mano
//  con esc().
export const aviso = (el: string, tipo: string, txt: unknown) => avisoHtml(el, tipo, esc(txt));
export const avisoHtml = (el: string, tipo: string, html: string) =>
  $(el).innerHTML = `<div class="aviso a-${tipo}">${html}</div>`;
export const limpiar = (el: string) => $(el).innerHTML = '';

//  Repintar un `<select>` sin perderle a la persona lo que ya eligió (regla
//  12 del README). Asignar `.value` **no dispara `onchange`**: la pérdida es
//  silenciosa (en Recepción, quedarse sin proveedor a mitad de un remito).
export const repintar = (sel: string, html: string) => {
  const antes = $(sel).value;
  $(sel).innerHTML = html;
  if (antes && Array.from($(sel).options).some(o => o.value === antes))
    $(sel).value = antes;
};
export const num = (n: number | null | undefined) => (n ?? 0).toLocaleString('es-AR');

//  Quién usa el sistema y qué puede hacer. Lo escribe sólo entrar() (en
//  arranque.ts) por estos dos setters: los módulos leen, no reasignan.
export type Permiso = { recurso: string; accion: string; alcance: string };
export let YO: any = null, PERMISOS: Permiso[] = [];
export const fijarYo = (u: any) => { YO = u; };
export const fijarPermisos = (ps: Permiso[]) => { PERMISOS = ps; };
//  ¿TENGO ESTE ROL? Por su CÓDIGO (`admin`, `admin_global`, `dueno`…), de
//  `v_mi_usuario.roles_codigo` (0094, D211). `YO.roles` son los NOMBRES que
//  se muestran («Admin global»): nunca se pregunta por ésos. Hasta la sesión
//  147 la pantalla buscaba códigos en `YO.roles` y en producción nunca los
//  encontraba (el simulador los daba al revés).
export const tengoRol = (...codigos: string[]) =>
  ((YO?.roles_codigo ?? []) as string[]).some(c => codigos.includes(c));

export const puede = (r: string, a: string) =>
  PERMISOS.some(p => p.recurso === r && p.accion === a);
//  Y CON QUÉ ALCANCE: `puede()` dice que tenés el permiso, no sobre cuántas
//  filas (D69). Con alcance `propio` el RLS recorta la lista, y "no hay
//  ninguna" es sobre lo tuyo: el caso vacío tiene que decirlo. Devuelve null
//  sin permiso, como `app.alcance()` en la base.
export const alcance = (r: string, a: string) =>
  PERMISOS.find(p => p.recurso === r && p.accion === a)?.alcance ?? null;
export const fecha = (t: string | null | undefined) => t ? new Date(t).toLocaleString('es-AR',
  { day:'2-digit', month:'2-digit', hour:'2-digit', minute:'2-digit' }) : '—';
//  Un `date` de la base (`aaaa-mm-dd`) como se lee acá: día/mes/año (D231).
//  Sin pasar por `Date`, que lo tomaría como medianoche UTC y en Buenos Aires
//  lo correría al día anterior.
export const dia = (d: string | null | undefined) => {
  const r = String(d ?? '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return r ? `${r[3]}/${r[2]}/${r[1]}` : (d ? String(d) : '—');
};

//  Las RPC devuelven jsonb (tipo generado `Json`, inútil para `data.mensaje`).
//  Este envoltorio verifica el NOMBRE de la función y de sus argumentos contra
//  los tipos generados y declara la respuesta como objeto libre.
type Funciones = Database['public']['Functions'];
export type Respuesta = { ok?: boolean; mensaje?: string; [k: string]: any };
export type ErrorRpc = { message: string; code?: string; details?: string; hint?: string };
//  EL QUINTO ESTADO, que no es una respuesta sino su ausencia: la promesa
//  RECHAZA (un `fetch` que revienta: red cortada, DNS, pestaña sin conexión).
//  El `await` TIRA, la función `cargar…` se corta a la mitad y la pantalla
//  queda MUDA.
//
//  La conversión vive ACÁ y no en cada pantalla (D88: un arreglo se
//  reintroduce a sí mismo; la lectura de mañana nacería sin el `try`). El
//  rechazo llega con la MISMA forma que ya se sabe leer, `{ data: null, error }`:
//  una causa nueva de que esto no se leyó.
//
//  El `code` es propio y no uno de PostgREST: las pantallas ramifican por
//  código y `hint` (D68); uno de los que usa la base dispararía una rama
//  (`conteo_incompleto`, el desvío ya ajustado) sobre una consulta que nunca
//  llegó.
export const RECHAZO = 'RECHAZO';
//  OJO (D92): `supabase-js` NO rechaza cuando la red se cae (tarda unos siete
//  segundos): CONTESTA `{ data: null, error: { message: 'TypeError: Failed to
//  fetch', code: '' }, status: 0 }`. Lo que lo distingue de PostgREST diciendo
//  que no es el `status: 0`. Por eso se miran las DOS cosas —promesa que
//  rechaza y respuesta con `status: 0`—; la segunda es la de producción.
const sinRespuesta = (r: any) => !!r && typeof r === 'object' && !!r.error && r.status === 0;
const motivoDe = (r: any, e?: any) =>
  String(e !== undefined ? (e?.message || e) : (r?.error?.message || 'sin respuesta'));
const rechazoLeido = (motivo: string) => ({ data: null, count: null, status: 0, error: {
  message: 'la consulta no llegó a contestar (' + motivo + ')',
  code: RECHAZO, details: null, hint: null } });
export const pedir = async <T>(consulta: PromiseLike<T>): Promise<T> => {
  let r: any;
  try { r = await consulta; }
  catch (e: any){ return rechazoLeido(motivoDe(null, e)) as unknown as T; }
  return (sinRespuesta(r) ? rechazoLeido(motivoDe(r)) : r) as T;
};

//  EL TOPE DEL SERVIDOR (D122). PostgREST corta en «Max rows» TODA respuesta
//  (tabla, vista o función `setof`) sin error ni aviso: mil filas enteras
//  pueden no ser todas. Vale 1.000 (panel de Supabase, Data API → Settings);
//  si se cambia allá, se cambia ACÁ y en `MAX_ROWS` del mock, y en ningún otro lugar.
export const TOPE_SERVIDOR = 1000;
//  Cuántas páginas lee `pedirTodo()` antes de rendirse y decir que no leyó
//  todo: evita que un error sea un bucle infinito.
export const MAX_PAGINAS = 20;
//  Código del error de una lectura que agotó las páginas: las pantallas lo
//  muestran como «no se pudo leer»; Comisiones lo distingue (achicar el período).
export const PARCIAL = 'PARCIAL';

//  EL ORDEN DE LO QUE LLEGÓ POR PÁGINAS. `pedirTodo` pagina por la CLAVE (ver
//  abajo): el orden de pantalla lo pone ella con `ordenarPor('codigo')`,
//  `ordenarPor('-ocurrido_en', '-id')`. El texto se compara con colación de
//  idioma como el mock y la base; los null van últimos, como en PostgreSQL.
//  Puede diferir en detalles de colación (la eñe): es orden de lectura.
const esInstante = (v: unknown): v is string =>
  typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v) && !Number.isNaN(Date.parse(v));
export const ordenarPor = (...campos: string[]) => (a: any, b: any): number => {
  for (const c of campos){
    const desc = c.startsWith('-'), k = desc ? c.slice(1) : c;
    const x = a[k] ?? null, y = b[k] ?? null;
    if (x === y) continue;
    if (x === null) return desc ? -1 : 1;
    if (y === null) return desc ? 1 : -1;
    //  Un instante se compara como instante: PostgreSQL omite la fracción cero
    //  (`12:00:00+00` vs `12:00:00.5+00`) y como texto el `.` va antes que el `+`.
    const r = esInstante(x) && esInstante(y) ? Date.parse(x) - Date.parse(y)
      : typeof x === 'string' && typeof y === 'string'
      ? x.localeCompare(y, 'es') : (x < y ? -1 : x > y ? 1 : 0);
    if (r) return desc ? -r : r;
  }
  return 0;
};

//  UNA LISTA QUE SE NECESITA ENTERA. Un catálogo o lo que está en viaje no se
//  puede mostrar «los primeros mil»: un selector incompleto no se nota y un
//  pendiente que no llegó no lo mueve nadie. Se pide de a páginas de
//  `TOPE_SERVIDOR`, hasta que llega una que no está llena.
//
//  DOS MANERAS DE PAGINAR, y la que se usa casi siempre es la primera:
//
//   · POR CLAVE (`por: 'id'`): cada página pide las mil siguientes DESPUÉS de
//     la última clave que llegó (`.gt(por, última).order(por).limit(mil)`).
//     La clave tiene que ser única y numérica. Ojo: paginar por posición («de
//     la 1.000 a la 1.999») pierde sin error la primera fila de la página 2
//     con cualquier BAJA entre páginas; «después de la clave N» no. El orden
//     en que se MUESTRA lo pone `orden` (un `ordenarPor`), sobre todo lo leído.
//
//   · POR POSICIÓN (sin `por`), para vistas sin clave única de una columna
//     (paradas, rendiciones pendientes, saldos por sesión). `armar` tiene que
//     traer un orden TOTAL y `clave` descarta la fila que un alta repite. Una
//     BAJA entre dos páginas puede perder una fila: son listas de una página.
//
//   · POR CLAVE ADENTRO DE UNA FUNCIÓN (`claveAdentro: true`, 0053): una
//     función `security definer` no deja entrar `.gt()` ni `.order()` de
//     afuera. Recibe la clave como parámetro (`p_despues`) y devuelve en orden
//     de clave: `armar(ultima)` la usa (null en la primera página) y
//     `pedirTodo` no agrega nada. Las Comisiones del período.
//
//  Devuelve lo mismo que `pedir()` —`{ data, error }`—. Un error en CUALQUIER
//  página es un error de toda la lectura: media lista no se muestra como la
//  lista. Si se agotan las `MAX_PAGINAS` con la última llena, también es un
//  error, con código `PARCIAL`.
//
//  `vigente`, si se da, se pregunta entre página y página: si ya nadie espera
//  la lectura devuelve `{ data: null, error: null }` y el llamador (que tiene
//  su guarda de respuesta vieja) la descarta.
export type Todo<F> = { data: F[] | null; error: any };
type Armable = {
  range: (desde: number, hasta: number) => PromiseLike<any>;
  order: (col: string, o?: any) => any;
};
export const pedirTodo = async <F = any>(
  armar: (ultima?: any) => Armable | PromiseLike<any>,
  opciones: { por?: string; orden?: (a: F, b: F) => number;
              clave?: (f: F) => unknown; vigente?: () => boolean;
              claveAdentro?: boolean } = {},
): Promise<Todo<F>> => {
  const { por, orden, clave, vigente, claveAdentro } = opciones;
  const filas: F[] = [], vistas = new Set<unknown>();
  let ultima: unknown = null;
  for (let p = 0; p < MAX_PAGINAS; p++){
    if (p > 0 && vigente && !vigente()) return { data: null, error: null };
    const consulta = por && claveAdentro
      ? armar(p > 0 ? ultima : null) as PromiseLike<any>
      : por
      ? (() => { let q: any = armar(); if (p > 0) q = q.gt(por, ultima);
                 return q.order(por).limit(TOPE_SERVIDOR); })()
      : (armar() as Armable).range(p * TOPE_SERVIDOR, (p + 1) * TOPE_SERVIDOR - 1);
    const r: any = await pedir(consulta);
    if (r.error) return { data: null, error: r.error };
    const pagina: F[] = r.data || [];
    for (const f of pagina){
      if (!por && clave){
        const k = clave(f);
        if (vistas.has(k)) continue;
        vistas.add(k);
      }
      filas.push(f);
    }
    if (por && pagina.length) ultima = (pagina[pagina.length - 1] as any)[por];
    if (pagina.length < TOPE_SERVIDOR)
      return { data: orden ? filas.sort(orden) : filas, error: null };
  }
  return { data: null, error: {
    message: `la lista tiene ${(TOPE_SERVIDOR * MAX_PAGINAS).toLocaleString('es-AR')} filas `
      + 'o más y no se leyó entera', code: PARCIAL, details: null, hint: null } };
};

//  EL DESFASE DE UNA ZONA en una fecha, como lo escribe ISO (`-03:00`), para
//  pedir un rango de `timestamptz` desde la medianoche de la EMPRESA (D39) y
//  no del navegador ni de UTC. Se mide al mediodía (el día de un cambio de
//  horario puede correrse una hora). null si el navegador no conoce la zona:
//  el llamador lo dice y usa el reloj local.
export const desfaseDe = (zona: string, fecha: string): string | null => {
  try {
    const t = new Intl.DateTimeFormat('en-US', { timeZone: zona, timeZoneName: 'longOffset' })
      .formatToParts(new Date(fecha + 'T12:00:00Z')).find(x => x.type === 'timeZoneName')?.value;
    const m = t?.match(/^GMT(?:([+-]\d{2}:\d{2}))?$/);
    return m ? (m[1] ?? '+00:00') : null;
  } catch { return null; }
};
//  Y el del navegador, para cuando la zona de la empresa no se pudo leer.
export const desfaseLocal = (fecha: string): string => {
  const min = -new Date(fecha + 'T12:00:00').getTimezoneOffset();
  const a = Math.abs(min);
  return `${min < 0 ? '-' : '+'}${String(Math.floor(a / 60)).padStart(2, '0')}:${String(a % 60).padStart(2, '0')}`;
};

//  Una ESCRITURA por tabla que no contesta (D91 + D92; son pocas, D73): una red
//  caída no debe decir "no se pudo" sobre algo que quizá quedó hecho. Mismo
//  texto que `rpc()` sin la última frase: qué mirar lo dice cada pantalla.
const rechazoEscrito = (motivo: string) => ({ data: null, count: null, status: 0, error: {
  message: 'la base no llegó a contestar (' + motivo + '), así que no se sabe si quedó hecho.',
  code: RECHAZO, details: null, hint: null } });
export const escribir = async <T>(consulta: PromiseLike<T>): Promise<T> => {
  let r: any;
  try { r = await consulta; }
  catch (e: any){ return rechazoEscrito(motivoDe(null, e)) as unknown as T; }
  return (sinRespuesta(r) ? rechazoEscrito(motivoDe(r)) : r) as T;
};
//  Y la pregunta que hacen los botones que no se pueden repetir sin daño: ¿la
//  base contestó? Por el código, no por el texto (D68).
export const noContesto = (error: { code?: string | null } | null | undefined) =>
  error?.code === RECHAZO;

//  EL BOTÓN QUE NO VUELVE (D91, D92). Un botón que crea algo cada vez que se
//  aprieta (cobro, gasto, pedido, remito) no puede volver a encenderse si la
//  base no contestó: el primer click quizá quedó hecho. Pero no se queda
//  muerto para siempre: volver a entrar a la pestaña (el camino que manda el
//  cartel) tiene que encenderlo de verdad.
//
//  Tres piezas que viven juntas:
//  - `trabarSiNoContesto(sel, error)`: si la base no contestó, apaga el botón
//    y lo anota.
//  - `trabado(sel)`: la usan los repintados de TODA la pantalla; sin ella
//    cada uno encendería el botón por su cuenta.
//  - `destrabarEn(seccion)`: la llama `arranque.ts` al entrar a una pestaña,
//    ANTES de las cargas. Sólo borra la anotación: quien vuelve a encender el
//    botón es la carga de la propia pestaña, con su regla. Cada botón tiene
//    su prueba de "volver a la pestaña lo enciende".
//
//  Sólo para botones FIJOS del HTML. Los que se pintan adentro de una lista o
//  panel nacen de nuevo en cada repintado: alcanza con
//  `if (!noContesto(error)) btn.disabled = false`.
//
//  El texto del botón cambia mientras está trabado y vuelve al suyo al
//  destrabarlo: el cartel que lo explica lo puede limpiar otra acción de la
//  tarjeta y quedaría un botón muerto sin explicación.
const TRABADOS = new Map<string, string>();
const TEXTO_TRABADO = 'No se sabe si quedó hecho: volvé a entrar a la pestaña';
export const trabarSiNoContesto = (sel: string,
    error: { code?: string | null } | null | undefined) => {
  if (!noContesto(error)) return false;
  const b = $(sel);
  if (!TRABADOS.has(sel)) TRABADOS.set(sel, b.textContent || '');
  b.disabled = true;
  b.textContent = TEXTO_TRABADO;
  return true;
};
export const trabado = (sel: string) => TRABADOS.has(sel);
export const destrabarEn = (seccion: string) => {
  const raiz = $(seccion);
  if (!raiz) return;
  for (const [sel, texto] of [...TRABADOS]){
    const b = $(sel);
    if (!b || !raiz.contains(b)) continue;
    TRABADOS.delete(sel);
    b.textContent = texto;
  }
};

//  La otra mitad de la puerta, la que mueve plata: una LLAMADA que rechaza.
//  Va adentro de `rpc()`, el único envoltorio de las llamadas de todas las
//  pantallas.
//
//  OJO CON EL TEXTO: una lectura que rechaza no se leyó, pero una ESCRITURA
//  que rechaza puede haber quedado hecha. Decir "no se pudo pagar" sobre algo
//  que quizá se pagó es afirmar de más; el texto dice lo único que se sabe
//  (que no contestó) y manda a mirar cómo quedó.
const TEXTO_RPC = (motivo: string) => 'la base no llegó a contestar (' + motivo + '), ' +
  'así que no se sabe si quedó hecho. Volvé a entrar a la pestaña para ver cómo quedó.';
//  PERO NO TODAS LAS LLAMADAS ESCRIBEN. Algunas sólo LEEN (`mis_cajas`,
//  `estado_del_corte`, `saldo_pedido`, `cobrado_del_item`, `estado_plata_admin`,
//  `lineas_recibidas`, `historial_costo`), y sobre una lectura el texto de
//  arriba es la D91 al revés: manda a buscar un movimiento que nunca existió.
//  Para ésas el texto es el de `pedir()`: no se leyó, y punto.
//  CUÁL VA POR CUÁL NO SE DECIDE POR EL NOMBRE (`datos_de_envio` suena a
//  consulta y hace un `update`): se mira `provolatile` de `pg_proc` (`s` es
//  `stable`, no escribe) y, si dice `v` (el valor por omisión, no prueba
//  nada), el cuerpo de `app.x`. Elegir mal es afirmar de más en una de las
//  dos direcciones.
const TEXTO_RPC_LEE = (motivo: string) => 'la consulta no llegó a contestar (' + motivo + ')';
type RespuestaRpc = { data: Respuesta | null; error: ErrorRpc | null };
const llamar = async (nombre: string, args: unknown, soloLee: boolean) => {
  type R = RespuestaRpc;
  const rechazo = (motivo: string) => ({ data: null, error: {
    message: (soloLee ? TEXTO_RPC_LEE : TEXTO_RPC)(motivo),
    code: RECHAZO, details: undefined, hint: undefined } }) as R;
  let r: any;
  try {
    r = await ((sb.rpc as any)(nombre, args) as PromiseLike<R>);
  } catch (e: any){
    return rechazo(motivoDe(null, e));
  }
  return sinRespuesta(r) ? rechazo(motivoDe(r)) : (r as R);
};
//  `rpc()` para las que ESCRIBEN (casi todas) y `rpcLee()` para las que sólo
//  consultan (Rentabilidad, comisiones del período, `mis_cajas`…). `tsc`
//  verifica nombre y argumentos en las dos.
export const rpc = <N extends keyof Funciones>(nombre: N, args?: Funciones[N]['Args']) =>
  llamar(nombre, args, false);
export const rpcLee = <N extends keyof Funciones>(nombre: N, args?: Funciones[N]['Args']) =>
  llamar(nombre, args, true);

//  LA CAJA DE CADA ACTO (D93, migración 0045). En UN SOLO LUGAR, como `pedir()`
//  y `rpc()` (D90): siete pantallas ofrecen botones que mueven plata, y la
//  pregunta "¿esta caja la puedo operar yo?" repetida es la D88.
//
//  La puerta la tiene la base (`app.exigir_caja` en las funciones de plata).
//  Esto sólo evita OFRECER un botón que se sabe que va a fallar; si no se
//  sabe, se ofrece igual.
//
//  Tres respuestas, no dos:
//    true   la base dijo que sí          → se ofrece
//    false  la base dijo que no          → no se ofrece, y se dice por qué
//    null   NO SE SABE                   → se ofrece igual
//  El null tiene cuatro causas y ninguna es "no te dejan": la llamada no se
//  pudo leer, no hay ninguno de los permisos que la abren, la fila no vino, o
//  la configuración apunta a una caja que no está (regla del 4/9). Cerrar por
//  cualquiera sería cerrar de más, que también es un bug.
export type FilaMisCajas = {
  acto: string; codigo: string; caja_id: number | null; caja: string | null;
  restringida: boolean | null; la_puedo_operar: boolean | null;
};
export let MIS_CAJAS: FilaMisCajas[] = [], E_MIS_CAJAS = '';

//  Los SIETE permisos de la guarda de `public.mis_cajas()` (el séptimo,
//  `proveedor.pagar`, es de la 0050: mantener la lista igual a la guarda). Sin
//  ninguno la función contesta 42501 A PROPÓSITO, así que ni se la llama: no
//  es una falla que mostrar, es la guarda funcionando.
const MUEVE_PLATA: [string, string][] = [
  ['caja','operar'], ['caja','arquear'], ['rendicion','recibir'],
  ['liquidacion','pagar'], ['gasto','registrar'], ['desvio','resolver'],
  ['proveedor','pagar']];
export const muevePlata = () => MUEVE_PLATA.some(([r, a]) => puede(r, a));

const leerMisCajas = async () => {
  if (!muevePlata()){ MIS_CAJAS = []; E_MIS_CAJAS = ''; return; }
  const { data, error } = await rpcLee('mis_cajas');
  E_MIS_CAJAS = error ? error.message : '';
  //  Devuelve un CONJUNTO de filas, no el jsonb de las demás funciones: por
  //  eso el casteo. `tsc` igual verifica el nombre y que no lleve argumentos.
  MIS_CAJAS = error ? [] : ((data as unknown as FilaMisCajas[]) || []);
};
//  Una sola lectura por sesión mientras ande, y se reintenta sola si falló
//  (como `catalogosCobro()`): volver a la pestaña es el reintento.
let PEDIDO_MIS_CAJAS: Promise<void> | null = null;
export const misCajas = () => (PEDIDO_MIS_CAJAS ??= leerMisCajas()
  .then(() => { if (E_MIS_CAJAS) PEDIDO_MIS_CAJAS = null; }));
//  El olvido explícito, para cuando la habilitación cambió en esta sesión
//  (la tarjeta "Las cajas" asigna y quita gente).
export const olvidarMisCajas = () => { PEDIDO_MIS_CAJAS = null; };

const filaActo = (acto: string) =>
  MIS_CAJAS.find(x => x.acto === acto)
  //  El cobro cae en la caja de su tipo de entrega, y si no está configurada,
  //  en la de `default`: la MISMA regla que `app.caja_del_pedido`.
  ?? (acto.startsWith('cobro:') ? MIS_CAJAS.find(x => x.acto === 'cobro:default') : undefined);

export const puedoActo = (acto: string): boolean | null =>
  filaActo(acto)?.la_puedo_operar ?? null;
export const cajaDelActo = (acto: string): string | null =>
  filaActo(acto)?.caja ?? null;
export const puedoOperar = (cajaId: number | null | undefined): boolean | null =>
  cajaId == null ? null
  : MIS_CAJAS.find(x => x.acto === 'caja' && Number(x.caja_id) === Number(cajaId))
      ?.la_puedo_operar ?? null;
export const nombreCaja = (cajaId: number | null | undefined): string | null =>
  cajaId == null ? null
  : MIS_CAJAS.find(x => x.acto === 'caja' && Number(x.caja_id) === Number(cajaId))?.caja ?? null;
//  Si la caja está restringida: un dato DE LA CAJA, en la misma fila (no usar
//  `v_cajas_habilitacion`, que tapa `caja.ver` y se cae por su cuenta).
//  `undefined` es "no hay fila para esa caja": ahí no se sabe, y decide quien pregunta.
export const cajaRestringida = (cajaId: number | null | undefined): boolean | null | undefined =>
  cajaId == null ? undefined
  : MIS_CAJAS.find(x => x.acto === 'caja' && Number(x.caja_id) === Number(cajaId))?.restringida;

//  El texto, en un solo lugar: nombra la caja, dice qué falta y adónde ir.
//  Devuelve HTML (lleva <b>): avisoHtml() o una plantilla, nunca aviso().
export const noHabilitado = (caja: string | null, verbo = 'operar') =>
  'No estás habilitado para ' + verbo + ' la caja <b>' + esc(caja || '—') + '</b>: ' +
  'un administrador te tiene que asignar a esa caja.';
